// react_app/src/components/admin/translations/TranslationToolbar.tsx

import { Download, Upload, Search } from 'lucide-react';
import { useTranslation } from '../../../i18n/useTranslation';
import type { LanguageDto } from '../../../api/translationService';

interface TranslationToolbarProps {
  languages: LanguageDto[];
  selectedLanguage: string;
  onLanguageChange: (culture: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  untranslatedOnly: boolean;
  onUntranslatedOnlyChange: (value: boolean) => void;
  onExport: () => void;
  onImport: () => void;
  exporting: boolean;
}

export function TranslationToolbar({
  languages,
  selectedLanguage,
  onLanguageChange,
  searchQuery,
  onSearchChange,
  untranslatedOnly,
  onUntranslatedOnlyChange,
  onExport,
  onImport,
  exporting,
}: TranslationToolbarProps) {
  const { t } = useTranslation();

  const targetLanguages = languages.filter(
    (l) => l.isEnabled && l.cultureName !== 'en'
  );

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      {/* Language selector */}
      <select
        value={selectedLanguage}
        onChange={(e) => onLanguageChange(e.target.value)}
        className="px-3 py-2 border border-neutral-200 rounded-md text-sm bg-white min-h-[44px]"
        aria-label={t('App.Admin.Translations.SelectLanguage')}
      >
        {targetLanguages.map((lang) => (
          <option key={lang.cultureName} value={lang.cultureName}>
            {lang.displayName} ({lang.cultureName})
          </option>
        ))}
      </select>

      {/* Search */}
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('App.Admin.Translations.SearchPlaceholder')}
          className="w-full pl-9 pr-3 py-2 border border-neutral-200 rounded-md text-sm min-h-[44px]"
        />
      </div>

      {/* Untranslated only */}
      <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer min-h-[44px]">
        <input
          type="checkbox"
          checked={untranslatedOnly}
          onChange={(e) => onUntranslatedOnlyChange(e.target.checked)}
          className="rounded border-neutral-300"
        />
        {t('App.Admin.Translations.UntranslatedOnly')}
      </label>

      {/* Export */}
      <button
        onClick={onExport}
        disabled={exporting}
        className="flex items-center gap-1.5 px-3 py-2 text-sm border border-neutral-200 rounded-md hover:bg-neutral-50 transition-colors min-h-[44px]"
      >
        <Download className="w-4 h-4" />
        {t('App.Admin.Translations.ExportCSV')}
      </button>

      {/* Import */}
      <button
        onClick={onImport}
        className="flex items-center gap-1.5 px-3 py-2 text-sm border border-neutral-200 rounded-md hover:bg-neutral-50 transition-colors min-h-[44px]"
      >
        <Upload className="w-4 h-4" />
        {t('App.Admin.Translations.ImportCSV')}
      </button>
    </div>
  );
}
